import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, CheckCircle2, Users } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import useDossierStore from '@/stores/useDossierStore';
import useClientStore from '@/stores/useClientStore';
import { formatDate, statusLabel } from '@/lib/utils';

export default function DashboardPage() {
  const { dossiers, loading: dossiersLoading, error: dossiersError, fetchDossiers } = useDossierStore();
  const { clients, total: clientsTotal, loading: clientsLoading, error: clientsError, fetchClients } = useClientStore();

  // Fetch data on mount — large limit to get enough data for stats
  useEffect(() => {
    fetchDossiers({ limit: 100 });
    fetchClients({ limit: 100 });
  }, [fetchDossiers, fetchClients]);

  const today = new Date();
  const formattedToday = today.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Compute stats from store data
  const dossiersEnCours = dossiers.filter((d) => d.statut === 'en_cours').length;
  const dossiersTermines = dossiers.filter((d) => d.statut === 'termine').length;
  const clientsCount = clientsTotal || clients.length;

  // Get 5 most recent dossiers sorted by date_mandat desc
  const recentDossiers = [...dossiers]
    .sort((a, b) => new Date(b.date_mandat) - new Date(a.date_mandat))
    .slice(0, 5);

  // Helper to get client name by ID
  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? client.nom : `Client #${clientId}`;
  };

  const loading = dossiersLoading || clientsLoading;
  const error = dossiersError || clientsError;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Tableau de bord</h1>
            <p className="text-sm text-slate-500 capitalize">{formattedToday}</p>
          </div>
          <Link to="/dossiers/nouveau">
            <Button variant="primary" size="lg">
              <Plus className="w-4 h-4" />
              Nouveau dossier
            </Button>
          </Link>
        </div>
        <p className="text-slate-600">Chargement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Tableau de bord</h1>
            <p className="text-sm text-slate-500 capitalize">{formattedToday}</p>
          </div>
          <Link to="/dossiers/nouveau">
            <Button variant="primary" size="lg">
              <Plus className="w-4 h-4" />
              Nouveau dossier
            </Button>
          </Link>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">Erreur: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tableau de bord</h1>
          <p className="text-sm text-slate-500 capitalize">{formattedToday}</p>
        </div>
        <Link to="/dossiers/nouveau">
          <Button variant="primary" size="lg">
            <Plus className="w-4 h-4" />
            Nouveau dossier
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Dossiers en cours</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{dossiersEnCours}</p>
            </div>
            <div className="p-3 bg-carex-100 rounded-lg">
              <FileText className="w-6 h-6 text-carex-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Dossiers terminés</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{dossiersTermines}</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Clients</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{clientsCount}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Dossiers */}
      <Card>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Dossiers récents</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-medium text-slate-700">Référence</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Client</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Statut</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Date</th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {recentDossiers.map((dossier) => (
                <tr key={dossier.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-900">{dossier.reference}</td>
                  <td className="py-3 px-4 text-slate-600">{getClientName(dossier.client_id)}</td>
                  <td className="py-3 px-4">
                    <Badge variant={dossier.statut}>{statusLabel(dossier.statut)}</Badge>
                  </td>
                  <td className="py-3 px-4 text-slate-600">{formatDate(dossier.date_mandat)}</td>
                  <td className="py-3 px-4 text-center">
                    <Link to={`/dossiers/${dossier.id}`} className="text-carex-600 hover:text-carex-700 font-medium">
                      Ouvrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
